import { BootScreen } from "./BootScreen.js";
import L from "leaflet";
import {
    connectionState,
    netherCoordinates,
    distance,
    locationUrl,
    freshTelemetry,
    worldClock,
    regionMetrics,
    resourcePercent,
} from "./model.js";
import "../../css/schwiizmc.css";

import { labels, stateLabel, worldLabel } from "./locale.js";
const icons = {
    overview: "M3 3h7v7H3z M14 3h7v7h-7z M3 14h7v7H3z M14 14h7v7h-7z",
    players:
        "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2 M13 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0 M17 4a4 4 0 0 1 0 8 M22 21v-2a4 4 0 0 0-3-4",
    worlds: "M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0 M3 12h18 M12 3c5 5 5 13 0 18-5-5-5-13 0-18",
    layers: "m12 3 9 5-9 5-9-5z M3 12l9 5 9-5 M3 16l9 5 9-5",
    locations: "M19 10c0 5-7 11-7 11S5 15 5 10a7 7 0 1 1 14 0 M14 10a2 2 0 1 1-4 0 2 2 0 0 1 4 0",
    tools: "m3 17 14-14 4 4L7 21z M7 13l3 3 M11 9l3 3 M15 5l3 3",
    settings:
        "M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8 M12 2v3 M12 19v3 M2 12h3 M19 12h3 M5 5l2 2 M17 17l2 2 M5 19l2-2 M17 7l2-2",
    share: "M14 3h7v7 M21 3 10 14 M10 4H4v16h16v-6",
    fullscreen: "M8 3H3v5 M16 3h5v5 M3 16v5h5 M21 16v5h-5",
};
const icon = (name) =>
    `<svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="${icons[name] || icons.overview}"/></svg>`;
const el = (tag, text, cls) => {
    const n = document.createElement(tag);
    if (text !== undefined) n.textContent = text;
    if (cls) n.className = cls;
    return n;
};

export class SchwiizUI {
    constructor(s) {
        this.s = s;
        this.boot = new BootScreen(s.map);
        this.feeds = new Map();
        this.points = [];
        this.measureLayer = L.layerGroup().addTo(s.map);
        this.coords = null;
        this.section = "overview";
        try {
            document.documentElement.dataset.theme = localStorage.getItem("schwiiz-theme") || "dark";
        } catch {}
        this.root = el("div", undefined, "sm-app");
        this.root.innerHTML = `<header class="sm-top"><a class="sm-brand" href="https://schwiizmc.ch" target="_blank" rel="noopener"><img src="images/schwiizmc-logo.png" alt="SchwiizMC"><span>SchwiizMC <em>Live Map</em></span></a><span class="sm-experimental">Experimental</span><div class="sm-top-status"><span class="sm-live" data-state="connecting">VERBINDE …</span><span class="sm-count">Spielerzahl wird geladen</span><details class="sm-performance"><summary aria-label="Performance anzeigen"><span class="sm-tps">TPS —</span><span aria-hidden="true">⌄</span></summary><div class="sm-region-popover"><div class="sm-region-heading"><strong>Performance</strong><span>15 Sek.</span></div><div class="sm-resources"></div><div class="sm-region-body"></div></div></details></div><a class="sm-site" href="https://schwiizmc.ch" target="_blank" rel="noopener">schwiizmc.ch ↗</a><button class="sm-icon" data-action="fullscreen" aria-label="Vollbild umschalten">${icon("fullscreen")}</button></header>
        <nav class="sm-rail" aria-label="Kartennavigation">${Object.keys(labels)
            .map(
                (k) =>
                    `<button data-section="${k}" title="${labels[k]}" aria-label="${labels[k]}">${icon(k)}<span>${labels[k]}</span></button>`,
            )
            .join("")}<span class="sm-rail-sign" aria-hidden="true">CH</span></nav>
        <aside class="sm-sidebar" aria-label="Kartenbereich"><div class="sm-panel-heading"><span>KARTE</span><button data-action="menu" aria-label="Seitenbereich schliessen" aria-expanded="true">×</button></div><div class="sm-panel"></div></aside>

        <button class="sm-clock" data-action="worlds" aria-label="Weltzeit und Weltauswahl"><span class="sm-clock-dial" aria-hidden="true"><span class="sm-clock-orbit"></span><span class="sm-clock-symbol">☀</span></span><span><small class="sm-clock-world">Weltzeit</small><strong class="sm-clock-time">—:—</strong><small class="sm-clock-phase">Wird geladen</small></span></button><div class="sm-hud"><span class="sm-crosshair" aria-hidden="true">+</span><button data-action="coordinates" title="Koordinaten kopieren"><span class="sm-eyebrow">KOORDINATEN</span><b>Wähle einen Punkt</b></button><span class="sm-zoom">Zoom —</span><button data-action="share" aria-label="Kartenausschnitt teilen" title="Kartenausschnitt teilen">${icon("share")}</button></div><div class="sm-toast" role="status" aria-live="polite"></div>`;
        document.body.append(this.root);
        this.root.inert = this.boot.active;
        this.panel = this.root.querySelector(".sm-panel");
        this.root.addEventListener("click", (e) => {
            const b = e.target.closest("button");
            if (!b) return;
            if (b.dataset.section) {
                if (this.section === b.dataset.section && !this.root.classList.contains("sm-collapsed"))
                    void this.action("menu");
                else this.show(b.dataset.section);
            }
            if (b.dataset.action) void this.action(b.dataset.action);
        });
        this.fetchListener = (e) => {
            this.feeds.set(e.detail.url, {
                last: e.detail.ok ? Date.now() : this.feeds.get(e.detail.url)?.last || 0,
                failed: !e.detail.ok,
            });
            this.refresh();
        };
        window.addEventListener("squaremap:fetch", this.fetchListener);
        s.map.on("mousemove", (e) => this.setCoords(s.toPoint(e.latlng)));
        s.map.on("moveend zoomend", () => this.refresh());
        s.map.on("click", (e) => {
            this.setCoords(s.toPoint(e.latlng));
            if (!this.measuring) return;
            if (this.points.length === 2) this.clearMeasure();
            this.points.push(e.latlng);
            L.circleMarker(e.latlng, { radius: 5, color: "#ff5661", fillOpacity: 1 }).addTo(this.measureLayer);
            if (this.points.length === 2) {
                L.polyline(this.points, { color: "#ff5661", weight: 2, dashArray: "6 6" }).addTo(this.measureLayer);
                this.toast(
                    `${distance(s.toPoint(this.points[0]), s.toPoint(this.points[1])).toFixed(1)} Blöcke · Luftlinie`,
                );
            }
            this.updateMeasure();
        });
        this.keyListener = (e) => {
            if (this.boot.active) return;
            if (
                e.ctrlKey ||
                e.metaKey ||
                e.altKey ||
                /INPUT|SELECT|TEXTAREA/.test(e.target.tagName) ||
                e.target.isContentEditable
            )
                return;
            if (e.key === "/") {
                e.preventDefault();
                this.show("players");
                this.panel.querySelector("input")?.focus();
            }
            if (e.key === "Escape") {
                this.root.classList.add("sm-collapsed");
                this.root.querySelector(".sm-performance").open = false;
                this.measuring = false;
                this.clearMeasure();
                this.updateMeasure();
            }
            if (e.key.toLowerCase() === "f") void this.action("fullscreen");
        };
        document.addEventListener("keydown", this.keyListener);
        this.outsideListener = (event) => {
            const details = this.root.querySelector(".sm-performance");
            if (!details.contains(event.target)) details.open = false;
        };
        document.addEventListener("pointerdown", this.outsideListener);
        this.show("overview");
        {
            this.root.classList.add("sm-collapsed");
            this.root.querySelector("[data-action=menu]").setAttribute("aria-expanded", "false");
        }
        this.timer = setInterval(() => this.refresh(), 1000);
        if (import.meta.env.DEV && new URLSearchParams(location.search).has("qa")) {
            const banner = el("div", "TESTDATEN · KEIN LIVE-SERVER", "sm-qa");
            this.root.append(banner);
        }
        void this.pollTelemetry();
    }
    setCoords(p) {
        if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) return;
        this.coords = p;
        this.root.querySelector(".sm-hud b").textContent =
            `X ${Math.floor(p.x).toLocaleString()}   Z ${Math.floor(p.y).toLocaleString()}`;
    }
    toast(text) {
        const n = this.root.querySelector(".sm-toast");
        n.textContent = text;
        n.classList.add("visible");
        clearTimeout(this.toastTimer);
        this.toastTimer = setTimeout(() => n.classList.remove("visible"), 4000);
    }
    async copy(text) {
        try {
            await navigator.clipboard.writeText(text);
            this.toast("In die Zwischenablage kopiert");
        } catch {
            this.toast("Markiere und kopiere den Text unten. Die Zwischenablage ist hier nicht verfügbar.");
            const input = el("input");
            input.value = text;
            input.readOnly = true;
            input.setAttribute("aria-label", "Text zum Kopieren");
            this.panel.append(input);
            this.root.classList.remove("sm-collapsed");
            input.select();
        }
    }
    async action(name) {
        const s = this.s;
        if (name === "menu") {
            this.root.classList.toggle("sm-collapsed");
            this.root
                .querySelector('[data-action="menu"]')
                .setAttribute("aria-expanded", String(!this.root.classList.contains("sm-collapsed")));
        }
        if (name === "settings") this.show("settings");
        if (name === "worlds") this.show("worlds");
        if (name === "fullscreen") {
            try {
                if (document.fullscreenElement) await document.exitFullscreen();
                else await document.documentElement.requestFullscreen();
            } catch {
                this.toast("Vollbild ist in diesem Browser nicht verfügbar.");
            }
        }
        if (name === "coordinates") {
            if (this.coords) await this.copy(`${Math.floor(this.coords.x)} ~ ${Math.floor(this.coords.y)}`);
        }
        if (name === "share") {
            if (s.worldList?.curWorld?.zoom)
                await this.copy(
                    locationUrl(
                        location.href,
                        s.worldList.curWorld.name,
                        s.toPoint(s.map.getCenter()),
                        s.map.getZoom(),
                    ),
                );
            else this.toast("Warte, bis die Karte geladen ist.");
        }
    }
    show(section) {
        this.section = section;
        this.root.classList.remove("sm-collapsed");
        this.root.querySelector("[data-action=menu]").setAttribute("aria-expanded", "true");
        this.root.querySelectorAll("[data-section]").forEach((b) => {
            b.classList.toggle("active", b.dataset.section === section);
            b.setAttribute("aria-current", b.dataset.section === section ? "page" : "false");
        });
        this.panel.replaceChildren();
        this.panel.scrollTop = 0;
        if (!matchMedia("(prefers-reduced-motion: reduce)").matches)
            this.panel.animate(
                [
                    { opacity: 0, transform: "translateY(5px)" },
                    { opacity: 1, transform: "translateY(0)" },
                ],
                { duration: 180, easing: "ease-out" },
            );
        this.panel.append(el("h2", labels[section]));
        if (section === "overview") {
            this.panel.insertAdjacentHTML(
                "beforeend",
                '<div class="sm-summary"><span>Sichtbare Spieler</span><strong class="sm-visible">—</strong></div><details class="sm-server-details"><summary>Server status</summary><div class="sm-health"></div></details>',
            );
        }
        if (section === "players") {
            const search = el("input");
            search.placeholder = "Spieler suchen …";
            search.type = "search";
            search.setAttribute("aria-label", "Sichtbare Spieler suchen");
            search.addEventListener("input", () => this.renderPlayers());
            this.panel.append(search, el("div", undefined, "sm-player-list"));
            this.renderPlayers();
        }
        if (section === "worlds") {
            const list = el("div", undefined, "sm-world-list");
            this.panel.append(list);
            this.renderWorlds();
        }
        if (section === "layers") {
            const b = el("button", "Ebenen auswählen", "sm-primary");
            b.onclick = () => {
                this.s.layerControl.controls?.expand();
                if (matchMedia("(max-width:700px)").matches) this.root.classList.add("sm-collapsed");
            };
            this.panel.append(b);
        }
        if (section === "locations") {
            const b = el("button", "Zum Weltstartpunkt", "sm-primary");
            b.onclick = () => {
                const w = this.s.worldList?.curWorld;
                if (w?.spawn) this.s.centerOn(w.spawn.x, w.spawn.z, w.zoom.def);
            };
            this.panel.append(b);
            const share = el("button", "Link zum Kartenausschnitt kopieren", "sm-secondary");
            share.onclick = () => this.action("share");
            this.panel.append(share);
        }
        if (section === "tools") this.tools();
        if (section === "settings") {
            const b = el("button", "Light / Dark Mode wechseln", "sm-primary");
            b.onclick = () => {
                const theme = document.documentElement.dataset.theme === "light" ? "dark" : "light";
                document.documentElement.dataset.theme = theme;
                try {
                    localStorage.setItem("schwiiz-theme", theme);
                } catch {}
            };
            this.panel.append(
                b,
                el("h3", "Tastenkürzel"),
                el("p", "/  Spieler suchen\nF  Vollbild\nEsc  Schliessen / Messung beenden", "sm-shortcuts"),
            );
        }
        this.refresh();
    }
    tools() {
        this.panel.insertAdjacentHTML(
            "beforeend",
            '<h3>Portal Calculator</h3><p class="sm-muted">Overworld / Nether · 8:1</p><div class="sm-inputs"><label>X<input name="x" type="number" value="0"></label><label>Z<input name="z" type="number" value="0"></label></div><label class="sm-select-label">Richtung<select><option value="nether">Overworld → Nether</option><option value="overworld">Nether → Overworld</option></select></label><output class="sm-result"></output><button class="sm-secondary sm-copy-result">Ergebnis kopieren</button><h3>Distance</h3><p class="sm-muted">Zwei Punkte wählen · Luftlinie in Blöcken</p><button class="sm-primary sm-measure">Messung starten</button><button class="sm-secondary sm-clear">Messung löschen</button><output class="sm-measure-result"></output>',
        );
        const calc = () => {
            const x = this.panel.querySelector("[name=x]"),
                z = this.panel.querySelector("[name=z]");
            this.result =
                x.value !== "" && z.value !== ""
                    ? netherCoordinates(
                          x.valueAsNumber,
                          z.valueAsNumber,
                          this.panel.querySelector("select").value === "nether",
                      )
                    : null;
            this.panel.querySelector(".sm-result").textContent = this.result
                ? `X ${this.result.x} · Z ${this.result.z}`
                : "Gültige Koordinaten eingeben";
        };
        this.panel.querySelectorAll("input,select").forEach((i) => i.addEventListener("input", calc));
        this.panel.querySelector(".sm-copy-result").onclick = () => {
            if (this.result) void this.copy(`${this.result.x} ~ ${this.result.z}`);
        };
        this.panel.querySelector(".sm-measure").onclick = () => {
            this.measuring = !this.measuring;
            this.updateMeasure();
            if (this.measuring) this.toast("Wähle zwei Punkte auf der Karte");
        };
        this.panel.querySelector(".sm-clear").onclick = () => {
            this.clearMeasure();
            this.updateMeasure();
        };
        calc();
        this.updateMeasure();
    }
    clearMeasure() {
        this.points = [];
        this.measureLayer.clearLayers();
    }
    updateMeasure() {
        const b = this.panel.querySelector(".sm-measure");
        if (b) b.textContent = this.measuring ? "Messung beenden" : "Messung starten";
        const o = this.panel.querySelector(".sm-measure-result");
        if (o)
            o.textContent =
                this.points.length === 2
                    ? `${distance(this.s.toPoint(this.points[0]), this.s.toPoint(this.points[1])).toFixed(1)} Blöcke`
                    : this.points.length === 1
                      ? "Wähle den zweiten Punkt"
                      : "";
    }
    renderPlayers() {
        const list = this.panel.querySelector(".sm-player-list");
        if (!list) return;
        const q = this.panel.querySelector("input").value.toLowerCase();
        const players = [...(this.s.playerList?.players.values() || [])]
            .filter((p) => p.name.toLowerCase().includes(q))
            .sort((a, b) => a.name.localeCompare(b.name));
        list.replaceChildren();
        for (const p of players) {
            const b = el("button", undefined, "sm-player");
            const head = el("img");
            head.src = p.getHeadUrl();
            head.alt = "";
            head.loading = "lazy";
            const body = el("span", p.name);
            body.append(el("small", worldLabel(this.s.worldList.worlds.get(p.world))));
            b.append(head, body, el("span", ["live", "static"].includes(this.playerState) ? "•" : "—", "sm-presence"));
            b.disabled = !p.position || !["live", "static"].includes(this.playerState);
            b.onclick = () => {
                this.s.playerList.showPlayer(p.uuid);
                if (matchMedia("(max-width:700px)").matches) this.root.classList.add("sm-collapsed");
            };
            list.append(b);
        }
        if (!players.length)
            list.append(
                el(
                    "p",
                    q ? "Keine passenden Spieler." : "Gerade sind keine Spieler auf der Karte sichtbar.",
                    "sm-empty",
                ),
            );
    }
    renderWorlds() {
        const list = this.panel.querySelector(".sm-world-list");
        if (!list) return;
        list.replaceChildren();
        for (const w of this.s.worldList?.worlds.values() || []) {
            const b = el("button", undefined, "sm-world-option");
            const img = el("img");
            img.src = this.s.worldList.getIcon(w);
            img.alt = "";
            b.append(img, el("span", worldLabel(w)));
            b.classList.toggle("selected", w === this.s.worldList.curWorld);
            b.onclick = () => this.s.worldList.showWorld(w.name, () => this.refresh());
            list.append(b);
        }
    }
    async pollTelemetry() {
        if (this.disposed) return;
        try {
            const res = await fetch("tiles/schwiizmc-telemetry.json", {
                cache: "no-store",
                signal: AbortSignal.timeout(5000),
            });
            this.telemetry = res.ok ? await res.json() : null;
        } catch {
            this.telemetry = null;
        }
        if (this.disposed) return;
        this.refresh();
        this.telemetryTimer = setTimeout(() => this.pollTelemetry(), 10000);
    }
    refresh() {
        const s = this.s,
            w = s.worldList?.curWorld;
        const f = this.feeds.get("tiles/players.json") || {
            failed: this.feeds.get("tiles/settings.json")?.failed,
        };
        const state = s.staticMode
            ? "static"
            : connectionState(
                  f.last,
                  f.failed,
                  Date.now(),
                  Math.max(15000, (w?.player_tracker?.update_interval || 1) * 3000),
              );
        const live = this.root.querySelector(".sm-live");
        live.dataset.state = state;
        live.textContent = stateLabel(state).toUpperCase();
        live.title = "Verbindung zu den Spielerdaten; keine Messung der Serverleistung";
        if (this.playerState !== state) {
            this.playerState = state;
            if (state === "offline" || state === "reconnecting") s.playerList?.clearPlayerMarkers();
            if (this.section === "players") this.renderPlayers();
        }
        const count = s.playerList?.jsonCache ? s.playerList.players.size : undefined;
        this.root.querySelector(".sm-count").textContent =
            count === undefined || !["live", "static"].includes(state)
                ? "Spielerzahl nicht verfügbar"
                : `${count} Spieler sichtbar`;
        const visible = this.panel.querySelector(".sm-visible");
        if (visible) visible.textContent = ["live", "static"].includes(state) ? (count ?? "—") : "—";
        this.root.querySelector(".sm-zoom").textContent = `Zoom ${s.map.getZoom() ?? "—"}`;
        if (this.world !== w?.name) {
            this.world = w?.name;
            this.clearMeasure();
            if (this.section === "worlds") this.renderWorlds();
        }
        document.body.classList.toggle("sm-low-zoom", s.map.getZoom() < 2);
        const t = freshTelemetry(this.telemetry) ? this.telemetry : null;
        this.renderPerformance(t);
        const clock = worldClock(t?.worldTimes?.[w?.name]);
        this.root.querySelector(".sm-clock-world").textContent = w ? worldLabel(w) : "Weltzeit";
        this.root.querySelector(".sm-clock-time").textContent = clock?.time || "—:—";
        this.root.querySelector(".sm-clock-phase").textContent = clock
            ? clock.night
                ? "Nacht"
                : "Tag"
            : "Keine Weltzeit";
        this.root.querySelector(".sm-clock-symbol").textContent = clock?.night ? "☾" : "☀";
        this.root.querySelector(".sm-clock").dataset.night = String(!!clock?.night);
        this.root.querySelector(".sm-clock-orbit").style.transform = `rotate(${clock?.angle || 0}deg)`;
        const health = this.panel.querySelector(".sm-health");
        if (health) {
            health.replaceChildren();
            const tile = this.feeds.get(`tiles/${w?.name}/changes.json`) || {};
            for (const [label, value] of [
                ["Spielerdaten", stateLabel(state)],
                ["Map updates", stateLabel(s.staticMode ? "static" : connectionState(tile.last, tile.failed))],
                ["Serververbindung", t ? "Aktuell" : "Nicht verfügbar"],
                ["Plattform", t?.platform || "Nicht verfügbar"],
                [
                    "Uptime",
                    Number.isFinite(t?.uptimeSeconds)
                        ? `${Math.floor(t.uptimeSeconds / 3600)} Std. ${Math.floor(t.uptimeSeconds / 60) % 60} Min.`
                        : "Nicht verfügbar",
                ],
                ["Slots", Number.isFinite(t?.capacity) ? String(t.capacity) : "Nicht verfügbar"],
                ["Minecraft", t?.minecraftVersion || "Nicht verfügbar"],
                [
                    "Weltzeit",
                    Number.isFinite(t?.worldTimes?.[w?.name])
                        ? t.worldTimes[w.name] % 24000 < 13000
                            ? "☀ Tag"
                            : "☾ Nacht"
                        : "Nicht verfügbar",
                ],
            ]) {
                const row = el("div");
                row.append(el("span", label), el("b", value));
                health.append(row);
            }
        }
        if (this.section === "players" && this.lastPlayers !== s.playerList?.jsonCache) {
            this.lastPlayers = s.playerList?.jsonCache;
            this.renderPlayers();
        }
    }
    renderPerformance(t) {
        const rows = regionMetrics(t);
        const measured = rows?.filter((r) => r.valid) || [];
        const min = measured.length ? Math.min(...measured.map((r) => r.tps)) : null;
        const summary = this.root.querySelector(".sm-tps");
        summary.textContent =
            min !== null
                ? `${min.toFixed(1)} TPS · Min.`
                : Number.isFinite(t?.tick?.tps)
                  ? `${t.tick.tps.toFixed(1)} TPS`
                  : "TPS —";
        summary.dataset.health =
            min === null ? "unknown" : min >= 19.5 ? "healthy" : min >= 16 ? "degraded" : "critical";
        const resources = this.root.querySelector(".sm-resources");
        const cpu = resourcePercent(t?.resources?.cpuPercent);
        const ram = resourcePercent(t?.resources?.heapPercent);
        resources.replaceChildren();
        for (const [label, value, detail] of [
            ["CPU", cpu, "JVM · alle CPU-Kerne"],
            ["RAM", ram, "Heap · vom Maximum"],
        ]) {
            const card = el("div", undefined, "sm-resource");
            card.append(el("span", label), el("strong", value === null ? "—" : `${value.toFixed(1)} %`));
            const meter = el("meter");
            meter.min = 0;
            meter.max = 100;
            meter.value = value ?? 0;
            meter.setAttribute(
                "aria-label",
                `${label}: ${value === null ? "nicht verfügbar" : `${value.toFixed(1)} Prozent`}`,
            );
            meter.hidden = value === null;
            card.append(meter, el("small", detail));
            resources.append(card);
        }
        const signature = JSON.stringify(rows);
        if (this.performanceSignature === signature) return;
        this.performanceSignature = signature;
        const body = this.root.querySelector(".sm-region-body");
        body.replaceChildren();
        if (!rows?.length) {
            body.append(el("p", rows ? "Keine aktiven Regionen" : "Keine Regionsdaten", "sm-empty"));
            return;
        }
        const table = el("table");
        const head = el("thead");
        const tr = el("tr");
        for (const text of ["Region", "TPS", "MSPT"]) {
            const th = el("th", text);
            th.scope = "col";
            tr.append(th);
        }
        head.append(tr);
        table.append(head);
        const tbody = el("tbody");
        for (const r of rows) {
            const row = el("tr");
            const name = el("td");
            const world = this.s.worldList?.worlds?.get(r.world);
            name.append(
                el("span", r.id === "global" ? "Global" : `#${r.id}`),
                el(
                    "small",
                    world
                        ? worldLabel(world)
                        : {
                              global: "Global tasks",
                              minecraft_overworld: "Overworld",
                              minecraft_the_nether: "Nether",
                              minecraft_the_end: "The End",
                          }[r.world] || r.world,
                ),
            );
            row.append(name, el("td", r.valid ? r.tps.toFixed(1) : "—"), el("td", r.valid ? r.mspt.toFixed(1) : "—"));
            row.dataset.health = !r.valid
                ? "unknown"
                : r.tps < 16
                  ? "critical"
                  : r.mspt >= 50 || r.tps < 19.5
                    ? "degraded"
                    : "healthy";
            tbody.append(row);
        }
        table.append(tbody);
        body.append(table);
        body.append(el("small", `${rows.length} Regionen · niedrigste TPS zuerst`, "sm-region-foot"));
    }
    dispose() {
        this.disposed = true;
        this.boot.dispose();
        clearInterval(this.timer);
        clearTimeout(this.telemetryTimer);
        clearTimeout(this.toastTimer);
        window.removeEventListener("squaremap:fetch", this.fetchListener);
        document.removeEventListener("keydown", this.keyListener);
        document.removeEventListener("pointerdown", this.outsideListener);
        this.root.remove();
    }
}
